import { useMemo, useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { useAuth } from '../../lib/auth.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import { relativeTime, absoluteTime } from '../../lib/format.js';
import { hasPermission } from '../../lib/permissions.js';
import type { CreatedApiToken, PermissionGroup } from '../../lib/types.js';

export function ApiTokensScreen(): ReactNode {
  const tokens = useQuery(() => api.apiTokens(), []);
  const catalogue = useQuery(() => api.permissionCatalogue(), []);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiToken | null>(null);

  async function revoke(id: string, name: string): Promise<void> {
    if (!confirm(`Revoke “${name}”? Any integration using it stops working immediately.`)) return;
    try {
      await api.deleteApiToken(id);
      toast.push('Token revoked', 'success');
      tokens.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Page
      title="API tokens"
      actions={
        <button className="btn primary" onClick={() => setCreating(true)}>
          New token
        </button>
      }
    >
      <p className="muted small" style={{ marginBottom: 12 }}>
        Bearer tokens for the public API (<code>/api/public/v1</code>). A token can only ever do what
        your own role allows — revoking your permission revokes the token's access too.
      </p>

      {tokens.loading && !tokens.data ? <Loading what="tokens" /> : null}
      {tokens.error ? <ErrorNote error={tokens.error} /> : null}

      {tokens.data ? (
        tokens.data.length === 0 ? (
          <EmptyState>No API tokens yet.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Abilities</th>
                  <th>Last used</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.data.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="mono small">
                      {t.abilities.includes('*') ? 'full access (*)' : t.abilities.join(', ')}
                    </td>
                    <td className="muted small" title={absoluteTime(t.lastUsedAt)}>
                      {relativeTime(t.lastUsedAt)}
                    </td>
                    <td className="muted small">{t.expiresAt ? absoluteTime(t.expiresAt) : 'never'}</td>
                    <td>
                      <button className="btn small danger" onClick={() => void revoke(t.id, t.name)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {creating ? (
        <CreateTokenModal
          groups={catalogue.data?.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreated(result);
            tokens.refetch();
          }}
        />
      ) : null}

      {created ? <SecretModal created={created} onClose={() => setCreated(null)} /> : null}
    </Page>
  );
}

function CreateTokenModal({
  groups,
  onClose,
  onCreated,
}: {
  groups: PermissionGroup[];
  onClose: () => void;
  onCreated: (result: CreatedApiToken) => void;
}): ReactNode {
  const { user } = useAuth();
  const held = user?.permissions ?? [];
  const toast = useToast();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Only offer permissions the current user actually has.
  const grantable = useMemo(
    () =>
      groups
        .map((g) => ({
          ...g,
          permissions: g.permissions.filter((p) => hasPermission(held, p.slug)),
        }))
        .filter((g) => g.permissions.length > 0),
    [groups, held],
  );

  function toggle(slug: string): void {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      const result = await api.createApiToken(name.trim(), [...picked]);
      onCreated(result);
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title="New API token" onClose={onClose} wide>
      <div className="stack">
        <label className="field">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CI media upload"
            autoFocus
          />
        </label>

        <div className="field">
          Abilities
          <div className="stack small" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {grantable.map((g) => (
              <div key={g.group}>
                <div className="faint">{g.group}</div>
                {g.permissions.map((p) => (
                  <label key={p.slug} className="row" style={{ gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={picked.has(p.slug)}
                      onChange={() => toggle(p.slug)}
                    />
                    <span className="mono small">{p.slug}</span>
                    <span className="muted small">{p.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={busy || name.trim() === '' || picked.size === 0}
            onClick={() => void submit()}
          >
            {busy ? 'Creating…' : 'Create token'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SecretModal({
  created,
  onClose,
}: {
  created: CreatedApiToken;
  onClose: () => void;
}): ReactNode {
  const toast = useToast();
  return (
    <Modal title="Copy your token now" onClose={onClose}>
      <p className="muted small">
        This is the only time the token is shown. Store it somewhere safe — if you lose it, revoke it
        and create a new one.
      </p>
      <div className="mono" style={{ wordBreak: 'break-all', padding: '10px 0' }}>
        {created.plainTextToken}
      </div>
      <div className="row between">
        <button
          className="btn small"
          onClick={() => {
            void navigator.clipboard?.writeText(created.plainTextToken).then(() => toast.push('Copied'));
          }}
        >
          Copy
        </button>
        <button className="btn primary small" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}
